/**
 * DocumentSession bridges the retained DOM and the ProseMirror editor state.
 * It owns everything needed to load, edit, and save one screenplay:
 *   - the parsed DOM (source of truth for all non-<paragraphs> sections),
 *   - resolved styles/settings + layout metrics,
 *   - the save pipeline (PM doc → model → serialize → zip).
 * React holds a reference to it; ProseMirror holds the live document state.
 */
import { EditorState, type Plugin } from "prosemirror-state";
import { history } from "prosemirror-history";
import type { ParsedDocument } from "../format/parse";
import type { DocSettings, Para, ScreenplayDoc, StyleDef } from "../format/types";
import { serializeDocument } from "../format/serialize";
import { writeFadein } from "../format/zip";
import { parasToDoc, docToParas } from "../editor/convert";
import { buildKeymap } from "../editor/keymap";
import { currentElementHighlight } from "../editor/decorations";
import { autocompletePlugin } from "../editor/autocomplete";
import { computeMetrics, type PageMetrics } from "../editor/metrics";

export class DocumentSession {
  readonly dom: Document;
  readonly styles: Map<string, StyleDef>;
  readonly settings: DocSettings;
  readonly metrics: PageMetrics;
  filename: string;
  private readonly initialParas: Para[];
  private _dirty = false;

  constructor(parsed: ParsedDocument, filename = "untitled.fadein") {
    this.dom = parsed.dom;
    this.styles = parsed.model.styles;
    this.settings = parsed.model.settings;
    this.metrics = computeMetrics(this.settings, this.styles);
    this.initialParas = parsed.model.paras;
    this.filename = filename;
  }

  get uuid(): string {
    return this.dom.getElementsByTagName("info")[0]?.getAttribute("uuid") ?? "";
  }

  get dirty(): boolean {
    return this._dirty;
  }
  markDirty(): void {
    this._dirty = true;
  }
  markSaved(): void {
    this._dirty = false;
  }

  /** Build the initial editor state. `extraPlugins` adds Phase-4 UI plugins. */
  createState(extraPlugins: Plugin[] = []): EditorState {
    return EditorState.create({
      doc: parasToDoc(this.initialParas),
      plugins: [
        // Autocomplete is first so it can intercept Enter/Tab only while open.
        autocompletePlugin(this.dom, this.settings.sceneTimeSeparator),
        history(),
        buildKeymap(this.styles),
        currentElementHighlight(),
        ...extraPlugins,
      ],
    });
  }

  /**
   * Convert the live PM doc back into a save-ready model. Stored text is left
   * exactly as-is: allcaps is a display concern (CSS), and the real Fade In
   * files store Character names in mixed case — mutating them would break the
   * round-trip preservation guarantee.
   */
  modelForSave(state: EditorState): ScreenplayDoc {
    return { paras: docToParas(state.doc), styles: this.styles, settings: this.settings };
  }

  /** Serialize the current editor state to a full document.xml string. */
  serialize(state: EditorState, pagecount?: number): string {
    const model = this.modelForSave(state);
    return serializeDocument(
      this.dom,
      model,
      pagecount !== undefined ? { pagecount } : {},
    );
  }

  /** Serialize + zip into .fadein bytes. */
  toBytes(state: EditorState, pagecount?: number): Uint8Array {
    return writeFadein(this.serialize(state, pagecount));
  }
}
